import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ActivityNextItem } from '@core/models/common/activity-next-item.model';
import { AuthService } from '@core/services/auth/auth.service';
import { QuestionSubmissionService } from '@core/services/question-submission/question-submission.service';

@Component({
  selector: 'app-activity-return',
  template: `
    <div class="min-h-[50vh] flex items-center justify-center px-4">
      <div class="text-center">
        <p *ngIf="loading" class="text-lg font-semibold text-gray-700">Resuming your activity…</p>
        <ng-container *ngIf="errorMessage">
          <p class="mb-4 text-red-600">{{ errorMessage }}</p>
          <button
            type="button"
            (click)="resolveNextItem()"
            class="font-semibold bg-green-500 text-white px-5 py-2 rounded-md"
          >
            Retry
          </button>
        </ng-container>
      </div>
    </div>
  `,
})
export class ActivityReturnComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage = '';
  private readonly subscriptions = new Subscription();
  private activityType = '';
  private activityUuid = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly questionSubmissionService: QuestionSubmissionService
  ) {}

  ngOnInit(): void {
    this.activityType = this.route.snapshot.paramMap.get('activityType') ?? '';
    this.activityUuid = this.route.snapshot.paramMap.get('activityUuid') ?? '';
    this.resolveNextItem();
  }

  resolveNextItem(): void {
    this.errorMessage = '';

    if (!this.activityUuid || !['course', 'conference'].includes(this.activityType)) {
      this.loading = false;
      this.errorMessage = 'This activity return URL is invalid.';
      return;
    }

    if (!this.authService.isAuthenticated()) {
      const returnUrl = this.router.url;
      this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
      return;
    }

    this.loading = true;
    const nextItemRequest: Observable<ActivityNextItem> =
      this.activityType === 'course'
        ? this.questionSubmissionService.getNextItem(this.activityUuid)
        : this.questionSubmissionService.getConferenceNextItem(this.activityUuid);

    const nextItemSub = nextItemRequest.subscribe({
      next: (nextItem) => {
        this.loading = false;
        if (!nextItem?.item_type) {
          this.navigateToActivity();
          return;
        }
        this.navigateToNextItem(nextItem);
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'The next activity item could not be loaded.';
      },
    });

    this.subscriptions.add(nextItemSub);
  }

  private navigateToActivity(): void {
    const activityUrl =
      this.activityType === 'course'
        ? `/courses/${this.activityUuid}`
        : `/live/${this.activityUuid}`;
    this.router.navigate([activityUrl]);
  }

  private navigateToNextItem(nextItem: ActivityNextItem): void {
    if (this.activityType === 'conference') {
      this.navigateToConferenceItem(nextItem);
      return;
    }

    if (nextItem.item_type === 'questionSet') {
      this.router.navigate([`/chapter/${this.activityUuid}/test/${nextItem.item_uuid}`]);
    } else if (nextItem.item_type === 'lesson') {
      this.router.navigate([`/chapter/${this.activityUuid}/lesson/${nextItem.item_uuid}`]);
    } else if (nextItem.item_type === 'certificate') {
      this.router.navigate([`/courses/${this.activityUuid}/certificate`]);
    } else if (nextItem.item_type === 'certified' && nextItem.item_uuid) {
      window.open(nextItem.item_uuid, '_blank');
    } else {
      this.errorMessage = 'The next activity item is not supported.';
    }
  }

  private navigateToConferenceItem(nextItem: ActivityNextItem): void {
    if (nextItem.item_type === 'questionSet') {
      this.router.navigate([`/credit/${this.activityUuid}/test/${nextItem.item_uuid}`]);
    } else if (nextItem.item_type === 'credit') {
      this.router.navigate([`/credit/${this.activityUuid}`]);
    } else if (nextItem.item_type === 'certificate') {
      this.router.navigate([`/live/${this.activityUuid}/certificate`]);
    } else if (nextItem.item_type === 'certified' && nextItem.item_uuid) {
      window.open(nextItem.item_uuid, '_blank');
    } else {
      this.errorMessage = 'The next activity item is not supported.';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
